import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import FilmCard from "@/components/feature/FilmCard";
import { films } from "@/mocks/films";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const FAVORITE_KEY = "krfilm:favorites";

const readFavorites = (): string[] => {
  try {
    const raw = window.localStorage.getItem(FAVORITE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === "string") : [];
  } catch {
    return [];
  }
};

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const film = films.find((item) => item.id === id);
  const [toast, setToast] = useState("");
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    setFavorites(readFavorites());
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useDocumentMeta({
    title: film ? `${film.title} - 韩国AV剧情介绍与资源` : "影视作品不存在 - 韩国AV",
    description: film
      ? `${film.title}（${film.year}）：${film.synopsis.slice(0, 88)}… 含导演、主演、类型、评分等资料与相关资源索引说明。`
      : "该影视作品不存在或已下架，可返回分类页浏览其他韩国影视资料。",
    keywords: film ? `${film.title},${film.genres.join(",")},韩国AV剧情介绍,影视资源,${film.originalTitle}` : "韩国AV,影视资源",
  });

  const related = useMemo(() => {
    if (!film) return [];
    return films
      .filter((item) => item.id !== film.id)
      .map((item) => ({
        film: item,
        score: item.genres.filter((genre) => film.genres.includes(genre)).length + (item.director === film.director ? 1 : 0),
      }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((entry) => entry.film);
  }, [film]);

  const toggleFavorite = () => {
    if (!film) return;
    const next = favorites.includes(film.id)
      ? favorites.filter((item) => item !== film.id)
      : [...favorites, film.id];
    setFavorites(next);
    try {
      window.localStorage.setItem(FAVORITE_KEY, JSON.stringify(next));
    } catch {
      setToast("浏览器存储不可用，收藏未保存");
      return;
    }
    setToast(next.includes(film.id) ? "已加入我的收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("作品链接已复制，可分享给朋友");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  if (!film) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[900px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-film-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-950">影视作品不存在或已下架</h1>
          <p className="mt-2 max-w-md text-[13px] leading-6 text-foreground-500">
            你访问的作品资料可能已被调整。可以返回分类页浏览其他韩国影视资料，或使用搜索查找作品名称。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              浏览全部作品
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-[13px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              前往搜索
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const isFavorite = favorites.includes(film.id);
  const episodeLabel = film.type === "剧集" ? `共 ${film.episodeCount} 集 · ${film.duration}` : film.duration;

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 pb-4 pt-6 md:px-6 lg:px-8">
        <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-[12px] text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <Link to="/category" className="transition-colors hover:text-primary-700">
            影视分类
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <Link
            to={`/category?genre=${encodeURIComponent(film.genres[0])}`}
            className="transition-colors hover:text-primary-700"
          >
            {film.genres[0]}
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-800">{film.title}</span>
        </nav>

        <section className="mt-4 overflow-hidden rounded-lg bg-primary-950">
          <div className="grid grid-cols-1 gap-6 p-5 md:grid-cols-[212px_minmax(0,1fr)] md:gap-8 md:p-7 lg:grid-cols-[236px_minmax(0,1fr)]">
            <div className="mx-auto w-[168px] shrink-0 md:mx-0 md:w-full">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-md bg-primary-900">
                <img
                  src={film.poster}
                  alt={`${film.title} 主封面`}
                  title={`${film.title} 韩国影视作品海报`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex items-center justify-center gap-1.5 rounded-md border border-primary-800 bg-primary-900/60 py-2 text-[11px] text-primary-200">
                <i className="ri-database-2-line" />
                资料编号 {film.id.slice(0, 8).toUpperCase()}
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-accent-500 px-2 py-1 text-[10px] font-semibold text-primary-950">{film.type}</span>
                {film.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="rounded border border-primary-700/80 bg-primary-900/50 px-2 py-1 text-[10px] text-primary-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-3 font-heading text-[28px] font-semibold leading-tight text-background-50 md:text-[36px]">
                {film.title}
              </h1>
              <p className="mt-1 text-[12px] font-light tracking-wide text-primary-300">{film.originalTitle}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-primary-200">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-primary-400" />
                  {film.year}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-primary-400" />
                  {episodeLabel}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-map-pin-line text-primary-400" />
                  {film.region}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-primary-400" />
                  {film.views} 次浏览
                </span>
                <span className="flex items-center gap-1 text-accent-400">
                  <i className="ri-star-fill text-[11px]" />
                  <span className="font-semibold">{film.rating.toFixed(1)}</span>
                  <span className="text-primary-400">/ 10</span>
                </span>
              </div>

              <p className="mt-4 max-w-2xl border-l-2 border-accent-500 pl-3 text-[13px] leading-7 text-background-100">
                {film.tagline}
              </p>

              <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2 text-[12px] sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="w-12 shrink-0 text-primary-400">导演</dt>
                  <dd className="text-primary-100">{film.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-12 shrink-0 text-primary-400">状态</dt>
                  <dd className="text-primary-100">{film.status}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="w-12 shrink-0 text-primary-400">主演</dt>
                  <dd className="text-primary-100">{film.cast.join(" / ")}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-12 shrink-0 text-primary-400">类型</dt>
                  <dd className="text-primary-100">{film.genres.join(" / ")}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-12 shrink-0 text-primary-400">更新</dt>
                  <dd className="text-primary-100">{film.updatedAt}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={toggleFavorite}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-[13px] font-medium transition-colors ${
                    isFavorite
                      ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                      : "border border-primary-700 bg-primary-900/60 text-background-100 hover:border-accent-400 hover:text-accent-300"
                  }`}
                >
                  <i className={isFavorite ? "ri-bookmark-fill text-sm" : "ri-bookmark-line text-sm"} />
                  {isFavorite ? "已收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-700 bg-primary-900/60 px-4 py-2.5 text-[13px] text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-share-forward-line text-sm" />
                  分享链接
                </button>
                <Link
                  to={`/category?genre=${encodeURIComponent(film.genres[0])}`}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-700 bg-primary-900/60 px-4 py-2.5 text-[13px] text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-price-tag-3-line text-sm" />
                  同类作品
                </Link>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto mt-8 w-full max-w-[920px]">
          <article>
            <div className="flex items-center gap-2">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              <h2 className="font-heading text-[19px] font-semibold text-foreground-950">剧情简介</h2>
            </div>
            <p className="mt-4 text-[14px] leading-8 text-foreground-700">{film.synopsis}</p>

            <div className="mt-6 rounded-lg border border-background-200/90 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 text-[14px] font-semibold text-foreground-900">
                <i className="ri-book-open-line text-primary-600" />
                详细介绍
              </h3>
              <div className="mt-3 space-y-3.5">
                {film.details.map((paragraph, index) => (
                  <p key={paragraph.slice(0, 12)} className="text-[13px] leading-8 text-foreground-600">
                    <span className="mr-2 font-heading text-[12px] text-accent-700">0{index + 1}</span>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50/70 p-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded bg-accent-500 text-primary-950">
                  <i className="ri-links-line text-sm" />
                </span>
                <h3 className="text-[14px] font-semibold text-accent-900">资源索引说明</h3>
                <span className="rounded bg-background-50 px-2 py-0.5 text-[10px] text-accent-800">演示状态</span>
              </div>
              <p className="mt-3 text-[12px] leading-7 text-accent-900/85">
                本站不存储、不提供任何影片文件，也不提供实际播放或下载服务。以下条目仅用于展示「资源索引」的界面样式与交互流程，点击不会跳转到真实资源地址。
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["高清线路 A", "高清线路 B", "字幕文件索引"].map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setToast("当前为演示状态，未接入真实资源接口")}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-3 py-2 text-[12px] text-accent-900 transition-colors hover:bg-accent-100"
                  >
                    <i className="ri-external-link-line text-xs" />
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
              <span className="text-[12px] text-foreground-500">所属分类：</span>
              {film.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category?genre=${encodeURIComponent(genre)}`}
                  className="rounded-md bg-secondary-100 px-2.5 py-1.5 text-[12px] text-secondary-800 transition-colors hover:bg-secondary-200"
                >
                  {genre}
                </Link>
              ))}
              {film.tags.map((tag) => (
                <span key={tag} className="rounded-md border border-background-300 px-2.5 py-1.5 text-[11px] text-foreground-500">
                  #{tag}
                </span>
              ))}
            </div>
          </article>
        </div>

        <section className="mt-10">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent-700">You May Also Like</span>
              <h2 className="mt-1.5 font-heading text-[20px] font-semibold text-foreground-950">相关推荐</h2>
              <p className="mt-1 text-[12px] text-foreground-500">根据题材与导演匹配，选择相似气质的作品。</p>
            </div>
            <Link to="/category" className="hidden items-center gap-1 text-[12px] text-foreground-500 hover:text-primary-700 sm:flex">
              更多作品
              <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>
          {related.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center text-[13px] text-foreground-500">
              暂无相关推荐，去分类页逛逛吧
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {related.map((item) => (
                <FilmCard key={item.id} film={item} />
              ))}
            </div>
          )}
        </section>
      </main>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 animate-toast-in">
          <div className="flex items-center gap-2 rounded-md bg-foreground-950 px-4 py-2.5 text-[12px] text-background-50">
            <i className="ri-checkbox-circle-line text-accent-400" />
            {toast}
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}